import type { OHLCVPoint } from '../../api/types';

export type TiingoFrequency =
    | '1min'
    | '5min'
    | '15min'
    | '1hour'
    | '4hour'
    | '12hour'
    | '1day';

const FREQUENCIES: readonly { minutes: number; freq: TiingoFrequency }[] = [
    { minutes: 1, freq: '1min' },
    { minutes: 5, freq: '5min' },
    { minutes: 15, freq: '15min' },
    { minutes: 60, freq: '1hour' },
    { minutes: 240, freq: '4hour' },
    { minutes: 720, freq: '12hour' },
    { minutes: 1440, freq: '1day' },
];

const TICKER_ALIAS: Readonly<Record<string, string>> = {
    GOLD: 'XAU',
};

const EARLIEST_ISO: Readonly<Record<string, string>> = {
    XAU: '2020-01-02',
};

export function backendTicker(ticker: string): string {
    return TICKER_ALIAS[ticker] ?? ticker;
}

export function clampStart(ticker: string, start: Date): Date {
    const iso = EARLIEST_ISO[backendTicker(ticker)];
    if (!iso) return start;
    const earliest = new Date(`${iso}T00:00:00Z`);
    return earliest > start ? earliest : start;
}

export function tiingoFrequency(resolutionMinutes: number): {
    freq: TiingoFrequency;
    minutes: number;
} {
    for (let i = FREQUENCIES.length - 1; i >= 0; i--) {
        const { minutes, freq } = FREQUENCIES[i];
        if (minutes <= resolutionMinutes && resolutionMinutes % minutes === 0) {
            return { freq, minutes };
        }
    }
    return { freq: '1min', minutes: 1 };
}

export function bucketPoints(
    points: readonly OHLCVPoint[],
    targetMinutes: number,
    fetchedMinutes: number
): OHLCVPoint[] {
    if (targetMinutes <= fetchedMinutes || points.length === 0) {
        return [...points];
    }
    const bucketMs = targetMinutes * 60_000;
    const buckets = new Map<number, OHLCVPoint[]>();
    for (const p of points) {
        const key = Math.floor(p.t.getTime() / bucketMs) * bucketMs;
        const list = buckets.get(key);
        if (list) list.push(p);
        else buckets.set(key, [p]);
    }
    return [...buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([key, group]) => ({
            t: new Date(key),
            open: group[0].open,
            high: Math.max(...group.map(p => p.high)),
            low: Math.min(...group.map(p => p.low)),
            close: group[group.length - 1].close,
            vol: group.reduce((sum, p) => sum + (p.vol || 0), 0),
        }));
}
